import { ComponentPropsWithRef } from "react";
import styled from "styled-components";
import { primary03, shade600 } from "../../styles/tokens";

// TEXT

// STYLES
export const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 14rem auto;
  align-items: start;
  gap: 2rem 1.5rem;
  padding: 2rem;
`;

export const StoryLimits = styled.div<{ $width?: string }>`
  position: relative;
  width: ${({ $width }) => $width};
  padding: 2rem 0;
`;

export const StoryLimit = styled.div<{ $width: string; $max?: boolean }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $width }) => $width};
  border: 1px dashed ${({ $max }) => ($max ? primary03 : shade600)};
  pointer-events: none;
`;

export const StoryLimitLabel = styled.span<{ $max?: boolean }>`
  position: absolute;
  top: 0.375rem;
  right: 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  white-space: nowrap;
  color: ${({ $max }) => ($max ? primary03 : shade600)};
`;

// DATA

// TYPES
export interface Props
  extends Pick<ComponentPropsWithRef<"input">, "onBlur" | "ref"> {
  /**
   * The name attribute for the select element.
   */
  name?: string;
  /**
   * The options available in the select dropdown.
   */
  options: SelectOption[];
  /**
   * Label for the select component.
   */
  label?: string;
  /**
   * ID for the select component.
   */
  id?: string;
  /**
   * Placeholder text to display when no selection has been made.
   */
  placeholder?: string;
  /**
   * Whether multiple selections are allowed.
   */
  multi?: boolean;
  /**
   * Whether the search functionality is enabled.
   */
  search?: boolean;
  /**
   * The currently selected value(s). This will make the component controlled.
   */
  selection?: (string | number)[];
  /**
   * The default selected value(s) when the component is uncontrolled.
   */
  defaultSelection?: (string | number)[];
  /**
   * Callback function to handle selection changes.
   */
  onChange?: (selection: (string | number)[]) => void;
  /**
   * The width of the select button.
   * @default 8.25rem
   */
  width?: string;
  /**
   * The maximum width of the select button.
   * When provided, the select button will grow wider when selections are added up until this value.
   * If provided maxWidth must be greater than or equal to width.
   * Note: maxWidth must be provided in a fixed CSS unit (px, rem, em, pt). % is not supported.
   */
  maxWidth?: string;
  /**
   * The number of rows to display in the select button.
   * @default 1
   */
  rows?: number;
  /**
   * The maximum height of the dropdown list.
   */
  listMaxHeight?: string;
  /**
   * Whether the dropdown list should match the width of the select button.
   */
  matchListWidth?: boolean;
  /**
   * Whether the select is disabled.
   */
  disabled?: boolean;
  /**
   * Emulate an error state on the select
   */
  hasError?: boolean;
  /**
   * Whether the select dropdown is floating or not.
   */
  floating?: boolean;
  /**
   * Whether to use the subtle styling variant for the select trigger button.
   * @default false
   */
  subtle?: boolean;
  /**
   * When true, renders the trigger at 2.5rem height instead of the default 3rem.
   */
  small?: boolean;
  /**
   * Whether the selection can be cleared. Only applicable for single select.
   * @default true
   */
  clearable?: boolean;
  /**
   * Optional nodeId for registering this select in a floating-ui tree. Only
   * necessary if this select is used within a floating context (e.g. inside a dropdown)
   */
  nodeId?: string;
}

export type SelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
  selectedLabel?: string;
};

export type SkeletonProps = Pick<
  Props,
  "placeholder" | "multi" | "rows" | "width"
>;

// CONSTANTS
export const ROOT_FONT_SIZE_PX = 16;
export const BORDER_WIDTH_PX = 1;
export const DEFAULT_WIDTH = 8.25;
export const PADDING_LEFT = 1.5;
export const PADDING_RIGHT = 1;
export const SMALL_PADDING_LEFT = 1;
export const SMALL_PADDING_RIGHT = 0.5;
export const CHEVRON_GAP = 0.5;
export const CHEVRON_WIDTH = 1.5;
export const INPUT_SPACE = 2;
export const ROW_HEIGHT = 2;
export const ROW_GAP = 0.5;

export const storyOptions: SelectOption[] = [
  { value: "licenses", label: "Licenses Access" },
  { value: "org-settings-read", label: "Organization Settings Read" },
  { value: "org-settings", label: "Organization Settings" },
  { value: "team-invite", label: "Team Invite" },
  { value: "statements", label: "Statements" },
  { value: "payouts", label: "Payouts" },
  { value: "music", label: "Music" },
  { value: "labs", label: "Labs" },
  { value: "reports", label: "Reports" },
  { value: "admin", label: "Admin", disabled: true },
];

export const storyFewSelections = ["licenses", "team-invite"];
export const storyManySelections = storyOptions
  .slice(0, 9)
  .map(({ value }) => value);

// FUNCTIONS
export const parseCssSizeToPx = (
  input: string | undefined,
): number | undefined => {
  if (!input) return undefined;

  if (input.endsWith("rem") || input.endsWith("em"))
    return parseFloat(input) * ROOT_FONT_SIZE_PX;

  if (input.endsWith("pt")) return parseFloat(input) * 1.333; // 1pt = 1.333px

  if (input.endsWith("px")) return parseFloat(input);

  return undefined;
};

export const isValidMaxWidth = (maxWidth: string | undefined): boolean => {
  if (!maxWidth) return false;

  return (
    maxWidth.endsWith("px") ||
    maxWidth.endsWith("rem") ||
    maxWidth.endsWith("em") ||
    maxWidth.endsWith("pt") ||
    maxWidth.endsWith("vw") ||
    maxWidth.endsWith("%")
  );
};

// ASSETS
